import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Stack,
  IconButton,
  alpha,
  useTheme,
  Typography,
  Autocomplete,
  InputAdornment,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  Backspace,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { selectContact } from "../../../state/contact/contactSlice";
import { selectActiveCall } from "../../../state/callSessions/callSessionsSlice";
import { initiateCallAction } from "../../../sagas/userAgent/initiateCall/action";
import { showSnackbar } from "../../../state/snackbar/snackbarSlice";
import PresenceAvatar from "../PresenceAvatar/PresenceAvatar";
import { playDTMFTone } from "../../../utils/audioUtils";

const DialpadOverlay = ({ onClose, mode, onAssistedTransfer }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const activeCall = useSelector(selectActiveCall);
  const { contact } = useSelector(selectContact);
  const [inputValue, setInputValue] = useState("");
  const [transferType, setTransferType] = useState("blind");
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const getOverlayTitle = () => {
    switch (mode) {
      case "addCall":
        return "Add Call";
      case "transfer":
        return "Transfer Call";
      case "dialpad":
        return "DTMF";
      default:
        return "";
    }
  };

  const handleTransferTypeChange = (event, newTransferType) => {
    if (newTransferType !== null) {
      setTransferType(newTransferType);
    }
  };

  const handleButtonClick = (value) => {
    if (inputRef.current) {
      const input = inputRef.current;
      const { selectionStart, selectionEnd } = input;
      const newValue =
        inputValue.slice(0, selectionStart) +
        value +
        inputValue.slice(selectionEnd);
      setInputValue(newValue);

      // Set cursor position after the inserted character
      setTimeout(() => {
        input.setSelectionRange(selectionStart + 1, selectionStart + 1);
      }, 0);
    } else {
      setInputValue((prevValue) => prevValue + value);
    }
    if (mode === "dialpad" && activeCall) {
      playDTMFTone();
      activeCall.sendDTMF(value);
      console.log(`Sending DTMF : ${value}`);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const filteredData = pastedText.replace(/[^0-9*#]/g, "");
    const input = inputRef.current;
    const { selectionStart, selectionEnd } = input;
    const newValue =
      inputValue.slice(0, selectionStart) +
      filteredData +
      inputValue.slice(selectionEnd);
    setInputValue(newValue);
    setTimeout(() => {
      input.setSelectionRange(
        selectionStart + filteredData.length,
        selectionStart + filteredData.length
      );
    }, 0);
  };

  const handleDelete = () => {
    if (inputRef.current) {
      const input = inputRef.current;
      const { selectionStart, selectionEnd } = input;

      if (selectionStart === selectionEnd) {
        // If no text is selected, delete the character before the cursor
        const newValue =
          inputValue.slice(0, selectionStart - 1) +
          inputValue.slice(selectionStart);
        setInputValue(newValue);
        // Use useEffect to restore the cursor position after re-render
        setTimeout(() => {
          input.setSelectionRange(selectionStart - 1, selectionStart - 1);
        }, 0);
      } else {
        // If text is selected, delete the selected text
        const newValue =
          inputValue.slice(0, selectionStart) + inputValue.slice(selectionEnd);
        setInputValue(newValue);
        // Use useEffect to restore the cursor position after re-render
        setTimeout(() => {
          input.setSelectionRange(selectionStart, selectionStart);
        }, 0);
      }
      input.focus();
    }
  };

  const handleSubmit = () => {
    const cleanedPhoneNumber = inputValue.replace(/[\s\-]/g, "");
    if (!cleanedPhoneNumber || /[^\d*#]/.test(cleanedPhoneNumber)) {
      return;
    }
    switch (mode) {
      case "addCall":
        dispatch(initiateCallAction({ phoneNumber: cleanedPhoneNumber }));
        console.log(`Initiating new call to ${cleanedPhoneNumber}`);
        break;
      case "transfer":
        if (activeCall) {
          if (transferType === "blind") {
            activeCall.transfer(cleanedPhoneNumber, true);
            dispatch(
              showSnackbar({
                message: "Transfer Completed",
                severity: "success",
              })
            );
          } else {
            onAssistedTransfer(cleanedPhoneNumber);
          }
        }
        break;
      default:
        break;
    }
    onClose();
  };

  // const dialpadButtons = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
  const dialpadButtons = [
    { number: "1", letters: "" },
    { number: "2", letters: "ABC" },
    { number: "3", letters: "DEF" },
    { number: "4", letters: "GHI" },
    { number: "5", letters: "JKL" },
    { number: "6", letters: "MNO" },
    { number: "7", letters: "PQRS" },
    { number: "8", letters: "TUV" },
    { number: "9", letters: "WXYZ" },
    { number: "*", letters: "" },
    { number: "0", letters: "" },
    { number: "#", letters: "" },
  ];

  const getButtonText = () => {
    switch (mode) {
      case "addCall":
        return "Call";
      case "transfer":
        return "Transfer";
      default:
        return "";
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.4),
        backdropFilter: "blur(8px)",
        borderRadius: 3,
        p: 2,
        width: "20rem",
      }}
    >
      <Typography
        variant="h5"
        component="h2"
        sx={{
          textAlign: "center",
          color: theme.palette.text.primary,
          fontWeight: 500,
        }}
      >
        {getOverlayTitle()}
      </Typography>

      {mode === "transfer" && (
        <ToggleButtonGroup
          value={transferType}
          size="small"
          exclusive
          onChange={handleTransferTypeChange}
          aria-label="transfer type"
          fullWidth
          sx={{ my: 1.5 }}
        >
          <ToggleButton
            value="blind"
            aria-label="blind transfer"
            sx={{
              "&.Mui-selected": {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText,
                "&:hover": {
                  backgroundColor: theme.palette.secondary.main,
                },
              },
            }}
          >
            {transferType === "blind" && (
              <CheckIcon sx={{ mr: 0.5, fontSize: "1.2rem" }} />
            )}
            Blind
          </ToggleButton>
          <ToggleButton
            value="assisted"
            aria-label="assisted transfer"
            sx={{
              "&.Mui-selected": {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText,
                "&:hover": {
                  backgroundColor: theme.palette.secondary.dark,
                },
              },
            }}
          >
            {transferType === "assisted" && (
              <CheckIcon sx={{ mr: 0.5, fontSize: "1.2rem" }} />
            )}
            Assisted
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      {mode !== "dialpad" ? (
        <Autocomplete
          freeSolo
          value={inputValue}
          onChange={(event, newValue) => {
            if (typeof newValue === "string") {
              setInputValue(newValue);
            } else if (newValue && newValue.defaultNumber) {
              setInputValue(newValue.defaultNumber);
            }
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          inputValue={inputValue}
          options={contact}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return option;
            }
            return `${option.first_name} ${option.last_name} - ${option.defaultNumber}`;
          }}
          renderOption={(props, option) => (
            <ListItem {...props} key={option.defaultNumber}>
              <ListItemAvatar>
                <PresenceAvatar
                  contactNumber={option.defaultNumber}
                  size={40}
                />
              </ListItemAvatar>
              <ListItemText
                primary={`${option.first_name} ${option.last_name}`}
                secondary={option.defaultNumber}
              />
            </ListItem>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              onPaste={handlePaste}
              fullWidth
              margin="normal"
              color="secondary"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <IconButton onClick={handleDelete} edge="end">
                    <Backspace />
                  </IconButton>
                ),
                sx: {
                  height: "50px",
                  borderRadius: 1,
                },
              }}
            />
          )}
          filterOptions={(options, params) => {
            return options.filter((option) =>
              `${option.first_name} ${option.last_name} ${option.defaultNumber}`
                .toLowerCase()
                .includes(params.inputValue.toLowerCase())
            );
          }}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.7),
                backdropFilter: "blur(10px)",
              }
            }
          }}

          ref={autocompleteRef}
        />
      ) : (
        <TextField
          inputRef={inputRef}
          value={inputValue}
          onChange={(e) => {
            const value = e.target.value;
            if (/^[0-9]*$/.test(value)) {
              setInputValue(value);
            }
          }}
          onKeyDown={(e) => {
            if (mode !== "dialpad") return;
            if (activeCall && /[0-9*#]/.test(e.key)) {
              playDTMFTone();
              activeCall.sendDTMF(e.key);
              console.log(`Sending DTMF via keyboard: ${e.key}`);
            }
          }}
          fullWidth
          margin="normal"
          color="secondary"
          InputProps={{
            endAdornment: (
              <IconButton onClick={handleDelete} edge="end">
                <Backspace />
              </IconButton>
            ),
            sx: {
              height: "50px",
              borderRadius: 1,
            },
          }}
        />
      )}

      <Grid container spacing={1} sx={{ mt: 1 }}>
        {dialpadButtons.map(({ number, letters }) => (
          <Grid item xs={4} key={number}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => handleButtonClick(number)}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                backgroundColor: theme.palette.text.secondary,
                color: theme.palette.secondary.contrastText,
                height: "3rem",
                width: "5.3rem",
                fontSize: "1rem",
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.secondary.light, 0.2),
                },
              }}
            >
              <Box
                sx={{
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  lineHeight: 1,
                  marginBottom: "0.3rem",
                }}
              >
                {number}
              </Box>
              <Box
                sx={{
                  fontSize: "0.7rem",
                  color: "gray",
                  visibility: letters ? "visible" : "hidden",
                  height: "1rem",
                }}
              >
                {letters || " "}
              </Box>
            </Button>
          </Grid>
        ))}
      </Grid>

      <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
        {mode !== "dialpad" && (
          <Button
            variant="contained"
            color="secondary"
            size="large"
            fullWidth
            sx={{
              color: theme.palette.text.primary,
              textTransform: "none",
              fontSize: "16px",
            }}
            onClick={handleSubmit}
          >
            {getButtonText()}
          </Button>
        )}
        <Button
          variant="contained"
          color="error"
          size="large"
          fullWidth
          sx={{
            textTransform: "none",
            fontSize: "16px",
          }}
          onClick={onClose}
        >
          Close
        </Button>
      </Stack>
    </Box>
  );
};

export default DialpadOverlay;
